import React, { useState, useEffect, useCallback } from 'react';
import { leaguesAPI } from '../../../services/apiService';
import { RefreshCw, AlertCircle } from 'lucide-react';
import PlayerLink from '../../../components/PlayerLink';
import OptimizerPlayerTile from './OptimizerPlayerTile';
import OptimizerStatGrid from './OptimizerStatGrid';
import OptimizerFlyout from './OptimizerFlyout';
import OptimizerGapAnalysis from './OptimizerGapAnalysis';
import OptimizerSequencer from './OptimizerSequencer';
import OptimizerTradeRecs from './OptimizerTradeRecs';
import OptimizerTradeDetail from './OptimizerTradeDetail';
import OptimizerLoadingBar from './OptimizerLoadingBar';
import OptimizerSegmented, { OptimizerIconButton, OptimizerToolbarDivider } from './OptimizerSegmented';
import { dynastyTokens } from '../../../services/colorService';
import { isPitcher, fmtSalary, fmtStat, getBasePosition, getPlayerCategoryStats, positionLabel } from './optimizerUtils';
import { isPitcherPosition } from '../../../utils/playerClassification';
import { usePlayerModal } from '../../../contexts/PlayerModalContext';

const colors = dynastyTokens.colors;

// Map a base position to a diamond slot, bottom-row slot, or null (excluded)
// Returns: { location: 'diamond'|'bottomRow'|'excluded'|null, slot: string|null }
const classifyPosition = (rosterPosition) => {
  const base = getBasePosition(rosterPosition);
  if (!base) return { location: null, slot: null };

  // PITCHERS — excluded from field view entirely
  if (isPitcherPosition(base)) return { location: 'excluded', slot: null };

  // DIAMOND positions — these go on the field
  if (base === 'C') {
    return rosterPosition === 'C_0'
      ? { location: 'diamond', slot: 'C' }
      : { location: 'bottomRow', slot: base };
  }
  if (['LF', 'CF', 'RF', 'OF'].includes(base)) return { location: 'diamond', slot: 'OF' };
  if (base === 'SS') return { location: 'diamond', slot: 'SS' };
  if (base === '2B') return { location: 'diamond', slot: '2B' };
  if (base === '3B') return { location: 'diamond', slot: '3B' };
  if (base === '1B') return { location: 'diamond', slot: '1B' };

  if (['MI', 'CI', 'UTIL', 'DH'].includes(base)) return { location: 'bottomRow', slot: base };
  if (base === 'bench') return { location: null, slot: null };
  return { location: 'bottomRow', slot: base };
};

// Diamond tile positions
const TILE_POSITIONS = {
  OF_0: { top: '16%', left: 'calc(50% - 32%)' },
  OF_1: { top: '5%', left: 'calc(50% - 15%)' },
  OF_2: { top: '5%', left: 'calc(50% + 15%)' },
  OF_3: { top: '16%', left: 'calc(50% + 32%)' },
  SS:   { top: '33%', left: 'calc(50% - 18%)' },
  '2B': { top: '33%', left: 'calc(50% + 18%)' },
  '3B': { top: '46%', left: 'calc(50% - 30%)' },
  '1B': { top: '46%', left: 'calc(50% + 30%)' },
  C:    { bottom: '14%', left: '50%' },
};

const OptimizerDashboard = ({ leagueId, league, user, userTeam, teams }) => {
  // DD-RO-GAPS-0727 item 5: the add/drop modal the flyout's Pick-up button opens (same flow app-wide).
  const { openPlayerModal } = usePlayerModal();
  // item 4: the FA candidate highlighted in the flyout — drives the gaps panel's 'With selection' basis.
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  // DD-RO-GAPS-CLUSTER-0728 4.2: the flyout SUBJECT (player whose alternatives are open) = the drop for the
  // with-selection swap. Declared here (above the standings effect) so it can drive the subject-out param.
  const [flyoutPlayer, setFlyoutPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [optimizerData, setOptimizerData] = useState(null);
  const [roster, setRoster] = useState([]);
  const [mode, setMode] = useState('balanced');
  const [viewMode, setViewMode] = useState('hitters');
  const [subView, setSubView] = useState('field');
  // DD-RO-UI-0727: the persistent SIDEBAR Category Gaps now read the SAME authoritative source the Gap
  // Analysis tab uses — getTeamCategoryStandings (calculate_rotisserie_standings on basis='current'), NOT the
  // projected `category_gaps` payload (which showed a projected finish gap AS IF it were the current gap:
  // SO "111 behind 2nd" when the current gap is 21). Default 'current' = the Rotisserie Standings page
  // exactly. 3-way toggle lives above the sidebar panel (Tony expects it there); does not persist across mounts.
  const [gapBasis, setGapBasis] = useState('current');
  const [gapStandings, setGapStandings] = useState(null);
  useEffect(() => {
    const tid = userTeam?.team_id;
    if (!leagueId || !tid) return;
    let alive = true;
    const selId = selectedCandidate?.ids?.mlb ?? null;
    const subjId = flyoutPlayer?.ids?.mlb ?? null;      // subject-out: the player being replaced
    const useBasis = (gapBasis === 'projected_selection' && selId == null) ? 'projected' : gapBasis;
    leaguesAPI.getTeamCategoryStandings(leagueId, tid, useBasis,
      useBasis === 'projected_selection' ? selId : null,
      useBasis === 'projected_selection' ? subjId : null)
      .then(r => { if (alive) setGapStandings(r); })
      .catch(() => { if (alive) setGapStandings(null); });
    return () => { alive = false; };
  }, [leagueId, userTeam, gapBasis, selectedCandidate, flyoutPlayer]);
  // DD-RO-BUGS2-0724 Bug 4: an Action Priority tile drills into the Sequences view pre-filtered
  // to that category. Seeded by a tile click; cleared when Sequences is reached via the tab.
  const [seedCategory, setSeedCategory] = useState(null);
  const [flyoutData, setFlyoutData] = useState(null);
  const [flyoutLoading, setFlyoutLoading] = useState(false);
  const [tradeDetailRec, setTradeDetailRec] = useState(null);
  const [targetedDirection, setTargetedDirection] = useState('acquire');
  const [targetedResults, setTargetedResults] = useState(null);
  const [targetedLoading, setTargetedLoading] = useState(false);
  const [targetedQuery, setTargetedQuery] = useState('');
  const [targetedDropdown, setTargetedDropdown] = useState([]);
  const [targetedHighlight, setTargetedHighlight] = useState(-1);
  const [targetedSelected, setTargetedSelected] = useState(null);
  const [targetedDropdownOpen, setTargetedDropdownOpen] = useState(false);
  const targetedSearchRef = React.useRef(null);
  const targetedTimeoutRef = React.useRef(null);
  // DD-FASEARCH-RACE-0730: latest-request guard for the targeted-trade autocomplete
  // (debounced single fetch — a 300ms-apart pair can still overwrite out of order).
  const targetedRequestIdRef = React.useRef(0);
  const targetedAbortRef = React.useRef(null);

  const totalTeams = teams?.length || 10;

  const loadData = useCallback(async () => {
    if (!userTeam?.team_id) return;
    setLoading(true);
    setError(null);
    try {
      const [optRes, rosterRes] = await Promise.all([
        leaguesAPI.getOptimizerFull(leagueId, userTeam.team_id, mode),
        leaguesAPI.getMyRosterCanonical(leagueId),
      ]);
      setOptimizerData(optRes);
      setRoster(rosterRes?.players || []);
    } catch (err) {
      console.error('Optimizer load error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load optimizer data');
    } finally {
      setLoading(false);
    }
  }, [leagueId, userTeam?.team_id, mode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // DD-RO-SELECTION-LOGIC-0728 Bug 1: a highlighted FA candidate only makes sense while ITS flyout is open. Any
  // time that context ends — closing the flyout (X) or switching to another field player — the selection must
  // clear so 'With selection' stops lighting up / showing a stale projection with nothing actively selected.
  // Also drop the gaps basis off 'projected_selection' (else the header keeps labeling "(With selection)" while
  // the effect silently falls back to projected data — an inconsistent, un-actionable state).
  const clearSelection = useCallback(() => {
    setSelectedCandidate(null);
    setGapBasis(prev => (prev === 'projected_selection' ? 'projected' : prev));
  }, []);

  const openFlyout = useCallback(async (player) => {
    const playerId = player.ids?.mlb;
    if (!playerId) return;
    clearSelection();          // switching subjects: the prior subject's FA selection no longer applies
    setFlyoutPlayer(player);
    setFlyoutData(null);
    setFlyoutLoading(true);
    try {
      // DD-RO-TRADEDETAIL-0725 (d): fetch subject-ANCHORED send trades (shape i — generated with THIS
      // player as the give-up anchor) alongside the FA alternatives, so the flyout's Trades tab shows
      // trades that actually involve him at the source (I2 enforced at generation), not team-wide trades
      // filtered after the fact. allSettled so a targeted failure never blanks the alternatives.
      const [altRes, sendRes] = await Promise.allSettled([
        leaguesAPI.getOptimizerAlternatives(leagueId, playerId, userTeam.team_id, mode),
        leaguesAPI.getTargetedTrades(leagueId, userTeam.team_id, playerId, 'send', mode),
      ]);
      const alt = altRes.status === 'fulfilled' ? (altRes.value || {}) : {};
      const subjectTrades = sendRes.status === 'fulfilled'
        ? ((sendRes.value?.trade_engine?.trade_sequences) || [])
        : [];
      // gate report (DD-RO-CLASSAUDIT-0725) so the flyout's empty state is honest when trades were withheld
      const subjectTradesGate = sendRes.status === 'fulfilled'
        ? (sendRes.value?.trade_engine?.gate || null) : null;
      setFlyoutData({ ...alt, subject_trades: subjectTrades, subject_trades_gate: subjectTradesGate });
    } catch (err) {
      console.error('Flyout load error:', err);
    } finally {
      setFlyoutLoading(false);
    }
  }, [leagueId, userTeam?.team_id, mode]);

  const closeFlyout = () => {
    setFlyoutPlayer(null);
    setFlyoutData(null);
    clearSelection();          // X-ing the flyout clears the selected candidate + drops 'With selection'
  };

  // Debounced autocomplete search for targeted trades
  const handleTargetedQueryChange = (e) => {
    const query = e.target.value;
    setTargetedQuery(query);
    setTargetedHighlight(-1);
    setTargetedSelected(null);

    if (targetedTimeoutRef.current) clearTimeout(targetedTimeoutRef.current);

    if (query.trim().length < 2) {
      setTargetedDropdown([]);
      setTargetedDropdownOpen(false);
      return;
    }

    targetedTimeoutRef.current = setTimeout(async () => {
      // Begin an authoritative request; abort the prior in-flight search and gate
      // every state write on this id being the latest (out-of-order guard).
      const requestId = ++targetedRequestIdRef.current;
      if (targetedAbortRef.current) {
        try { targetedAbortRef.current.abort(); } catch (_) { /* no-op */ }
      }
      const controller = new AbortController();
      targetedAbortRef.current = controller;
      try {
        const response = await leaguesAPI.getAllLeaguePlayers(leagueId, {
          search: query.trim(),
          limit: 12,
        }, { signal: controller.signal });
        if (requestId !== targetedRequestIdRef.current) return; // superseded — discard
        if (response.success && response.players) {
          const filtered = response.players.filter(p => {
            if (targetedDirection === 'acquire') {
              return p.league_context?.status === 'owned'
                && p.league_context?.team?.team_id !== userTeam?.team_id;
            } else {
              return p.league_context?.team?.team_id === userTeam?.team_id;
            }
          });
          setTargetedDropdown(filtered);
          setTargetedDropdownOpen(filtered.length > 0);
        }
      } catch (err) {
        if (controller.signal.aborted || requestId !== targetedRequestIdRef.current) return;
        console.error('Targeted search error:', err);
      }
    }, 300);
  };

  const handleTargetedSelect = async (player) => {
    const pid = player.ids?.mlb;
    if (!pid) return;
    setTargetedSelected(player);
    setTargetedQuery(player.info?.full_name || '');
    setTargetedDropdownOpen(false);
    setTargetedDropdown([]);
    setTargetedLoading(true);
    setTargetedResults(null);
    try {
      const res = await leaguesAPI.getTargetedTrades(
        leagueId, userTeam.team_id, pid, targetedDirection, mode
      );
      setTargetedResults(res);
    } catch (err) {
      console.error('Targeted trade error:', err);
    } finally {
      setTargetedLoading(false);
    }
  };

  const handlePickSelect = async (pick) => {
    setTargetedSelected({ info: { full_name: pick.label } });
    setTargetedQuery(pick.label);
    setTargetedDropdownOpen(false);
    setTargetedDropdown([]);
    setTargetedLoading(true);
    setTargetedResults(null);
    try {
      const res = await leaguesAPI.getTargetedTrades(
        leagueId, userTeam.team_id, null, 'acquire', mode, pick.round, pick.year
      );
      setTargetedResults(res);
    } catch (err) {
      console.error('Targeted pick trade error:', err);
    } finally {
      setTargetedLoading(false);
    }
  };

  const handleTargetedKeyDown = (e) => {
    if (!targetedDropdownOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setTargetedHighlight(prev => Math.min(prev + 1, targetedDropdown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setTargetedHighlight(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && targetedHighlight >= 0) {
      e.preventDefault();
      handleTargetedSelect(targetedDropdown[targetedHighlight]);
    } else if (e.key === 'Escape') {
      setTargetedDropdownOpen(false);
    }
  };

  const clearTargeted = () => {
    setTargetedResults(null);
    setTargetedSelected(null);
    setTargetedQuery('');
    setTargetedDropdown([]);
    setTargetedDropdownOpen(false);
  };

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClick = (e) => {
      if (targetedSearchRef.current && !targetedSearchRef.current.contains(e.target)) {
        setTargetedDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Build recommendation lookup: mlb_id -> recommendation
  const recMap = {};
  if (optimizerData?.recommendations) {
    for (const rec of optimizerData.recommendations) {
      const pid = rec.player?.ids?.mlb;
      if (pid) recMap[pid] = rec;
    }
  }

  // Indicator dot color for a player
  const getDotColor = (player) => {
    const pid = player.ids?.mlb;
    const rec = pid ? recMap[pid] : null;
    if (rec) {
      if (rec.move_type === 'pickup' && rec.score?.total_points_delta > 0) return colors.success;
      if (rec.move_type === 'trade') return colors.warning;
    }
    return colors.info;
  };

  // Partition roster by status
  const activePlayers = roster.filter(p => p.league_context?.roster?.status === 'active');
  const benchPlayers = roster.filter(p => p.league_context?.roster?.status === 'bench');
  const dlPlayers = roster.filter(p => {
    const s = p.league_context?.roster?.status;
    return s === 'disabled_list' || s === 'dl';
  });
  const minorsPlayers = roster.filter(p => p.league_context?.roster?.status === 'minors');

  // Partition active roster into: diamond, bottom row, pitchers
  const diamondPlayers = [];
  const bottomRowPlayers = [];
  const ofPlayers = [];
  const pitcherPlayers = [];

  for (const p of activePlayers) {
    const rp = p.league_context?.roster?.position;
    const { location, slot } = classifyPosition(rp);

    if (location === 'excluded') {
      pitcherPlayers.push(p);
      continue;
    }
    if (location === 'diamond' && slot === 'OF') {
      ofPlayers.push(p);
    } else if (location === 'diamond') {
      diamondPlayers.push({ ...p, _slot: slot });
    } else if (location === 'bottomRow') {
      bottomRowPlayers.push(p);
    }
  }

  ofPlayers.forEach((p, i) => {
    if (i < 4) {
      diamondPlayers.push({ ...p, _slot: `OF_${i}` });
    }
  });

  // --- Category gaps helpers ---
  const gapRankColor = (gap) => {
    const rank = gap.current_rank;
    const status = gap.status;
    if (rank <= 3 || status === 'stable' || status === 'dominant') return colors.success;
    if (rank <= 7 || status === 'improvable') return colors.warning;
    return colors.error;
  };

  const gapBgColor = (gap) => {
    const c = gapRankColor(gap);
    if (c === colors.success) return 'rgba(16,185,129,0.15)';
    if (c === colors.warning) return 'rgba(245,158,11,0.15)';
    return 'rgba(239,68,68,0.15)';
  };

  const orderedGaps = (type) => {
    const cats = optimizerData?.meta?.categories?.[type];
    if (!cats || !optimizerData?.category_gaps) return [];
    const catList = Array.isArray(cats) ? cats : Object.keys(cats);
    return catList.map(cat =>
      optimizerData.category_gaps.find(g => g.category === cat)
    ).filter(Boolean);
  };

  const computedStandings = optimizerData?.standings || [];
  // DD-RO-GAPS-CLUSTER-0728 4.1: the STANDINGS list FOLLOWS the basis toggle. gapStandings.standings carries
  // every team's total on the selected basis (Current/Projected/With selection); names are joined by team_id
  // from the payload/teams. NO static fallback — if the basis source is absent the list fails loud (below).
  const _basisLabel = gapBasis === 'current' ? 'Current' : gapBasis === 'projected' ? 'Projected' : 'With selection';
  const _nameById = {};
  [...computedStandings, ...(teams || [])].forEach(t => { if (t?.team_id && !_nameById[t.team_id]) _nameById[t.team_id] = t.team_name; });
  const basisStandings = (gapStandings?.standings || []).map(s => ({
    team_id: s.team_id, team_name: s.team_name || _nameById[s.team_id] || '—',
    total_points: s.total_points, rank: s.rank,
  }));

  // --- RENDER ---

  if (loading) {
    return <OptimizerLoadingBar isLoading={loading} />;
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', color: colors.error, gap: 8 }}>
        <AlertCircle style={{ width: 24, height: 24 }} />
        {error}
      </div>
    );
  }

  // Render a single gap row
  const ordSuffix = (n) => { if (!n) return '—'; const s = ['th','st','nd','rd']; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };

  const GapRow = ({ gap }) => {
    // DD-RO-UI-0727: rank + gap come from the AUTHORITATIVE standings on the selected basis
    // (getTeamCategoryStandings), NOT the projected category_gaps payload. Falls back to the embedded gap only
    // if the authoritative fetch has not landed.
    const auth = gapStandings?.categories?.[gap.category];
    const rank = auth?.rank ?? gap.current_rank;
    const gapToNext = auth?.gap_to_next ?? gap.gap_to_next;
    const color = gapRankColor({ ...gap, current_rank: rank });
    const bg = gapBgColor({ ...gap, current_rank: rank });
    const isTop = rank <= 1;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3.5px 0' }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: colors.neutral[300], width: 30, textAlign: 'right' }}>
          {gap.category}
        </span>
        <span style={{
          fontSize: 10, fontWeight: 700,
          width: 28, height: 18, borderRadius: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: bg, color,
        }}>
          {ordSuffix(rank)}
        </span>
        <span style={{ flex: 1, fontSize: 10, color: colors.neutral[400], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {isTop ? '' : gapToNext != null ? `${gapToNext} behind ${ordSuffix(rank - 1)}` : ''}
        </span>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }} />
      </div>
    );
  };

  const hittingGaps = orderedGaps('hitting');
  const pitchingGaps = orderedGaps('pitching');

  // Bullpen view for pitchers
  const BullpenView = ({ pitchers }) => {
    const leftLane = pitchers.slice(0, 5);
    const rightLane = pitchers.slice(5, 10);
    const rightSlots = [...rightLane];
    while (rightSlots.length < 5) rightSlots.push(null);

    const renderTile = (player) => {
      if (!player) {
        return (
          <div style={{
            border: `1px dashed ${colors.neutral[700]}`, borderRadius: 3,
            padding: '6px 8px', width: '90%', maxWidth: 170,
            height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 11, color: colors.neutral[600] }}>Empty slot</span>
          </div>
        );
      }
      const dotColor = getDotColor(player);
      return (
        <div
          onClick={() => openFlyout(player)}
          style={{
            background: 'rgba(41,37,36,0.95)', border: `1px solid ${colors.neutral[700]}`,
            borderRadius: 3, padding: '6px 8px', width: '90%', maxWidth: 170,
            cursor: 'pointer', position: 'relative', transition: 'border-color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(234,179,8,0.5)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = colors.neutral[700]}
        >
          <div style={{ position: 'absolute', top: -3, right: -3, width: 8, height: 8, borderRadius: '50%', background: dotColor }} />
          <div style={{ fontSize: 12, fontWeight: 600, color: colors.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <PlayerLink mlbId={player.ids?.mlb} name={player.info?.full_name || '—'} style={{ color: colors.white, fontSize: 12, fontWeight: 600 }} />
          </div>
          <div style={{ fontSize: 11, color: colors.neutral[400], marginTop: 1 }}>
            {positionLabel(player)} · {player.info?.mlb_team || '—'}
          </div>
          {(() => {
            const pStats = getPlayerCategoryStats(player, optimizerData?.meta?.categories);
            if (pStats.length === 0) {
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3, fontSize: 11, color: colors.neutral[400] }}>
                  <span>{fmtStat(player)}</span>
                  <span>{fmtSalary(player)}</span>
                </div>
              );
            }
            // Item 3: shared 2×3 grid (bullpen shell keeps its own empty-slot +
            // no-stats fallback above).
            return <OptimizerStatGrid stats={pStats} gap={8} marginTop={3} />;
          })()}
        </div>
      );
    };

    const renderLane = (slots) => (
      <div style={{
        flex: 1, background: '#8B5E3C', borderRadius: 10,
        border: '2px solid #6B4423', position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '20px 10px',
      }}>
        <div style={{
          width: 70, height: 70, borderRadius: '50%',
          background: 'rgba(107,68,35,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 8, flexShrink: 0,
        }}>
          <div style={{ width: 40, height: 5, background: colors.white, borderRadius: 2 }} />
        </div>
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          justifyContent: 'space-evenly', alignItems: 'center',
          width: '100%', padding: '4px 0',
        }}>
          {slots.map((p, i) => <React.Fragment key={i}>{renderTile(p)}</React.Fragment>)}
        </div>
        <div style={{
          width: 70, height: 70, borderRadius: '50%',
          background: 'rgba(107,68,35,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginTop: 8, flexShrink: 0,
        }}>
          <svg width="24" height="28" viewBox="0 0 24 28">
            <polygon points="0,0 24,0 24,16 12,28 0,16" fill="#fff" />
          </svg>
        </div>
      </div>
    );

    return (
      <div style={{
        position: 'absolute', top: '4%', bottom: '16%', left: '8%', right: '8%',
        display: 'flex', gap: 16,
      }}>
        {renderLane(leftLane)}
        {renderLane(rightSlots)}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', height: '100%', background: colors.neutral[900] }}>
      {/* LEFT: Header + Field card */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 12, gap: 10 }}>

        {/* Header bar — DD-RO-TOOLBAR-0728: every control is the same 28px segmented primitive */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, padding: '0 4px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: colors.white, margin: 0, whiteSpace: 'nowrap' }}>
              Roster Optimizer
            </h2>

            <OptimizerSegmented
              value={subView}
              onChange={(key) => { setSubView(key); setSeedCategory(null); }}
              options={[
                { key: 'field', label: 'Field View' },
                { key: 'gaps', label: 'Gap Analysis' },
                { key: 'sequences', label: 'Sequences' },
              ]}
            />

            <OptimizerToolbarDivider />

            <OptimizerSegmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                { key: 'hitters', label: 'Hitters' },
                { key: 'pitchers', label: 'Pitchers' },
              ]}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <OptimizerSegmented
              value={mode}
              onChange={setMode}
              options={[
                { key: 'balanced', label: 'Balanced' },
                { key: 'win_now', label: 'Win Now' },
                { key: 'dynasty', label: 'Dynasty' },
              ]}
            />
            <OptimizerIconButton onClick={loadData} title="Refresh optimizer">
              <RefreshCw style={{ width: 14, height: 14 }} />
            </OptimizerIconButton>
          </div>
        </div>

        {subView === 'field' ? (
        <div style={{ position: 'relative', overflow: 'hidden', background: '#1b4d1b', borderRadius: 8, border: `1px solid ${colors.neutral[700]}`, height: 'calc(100vh - 120px)', maxHeight: 920 }}>

          {/* Hitters/Pitchers toggle moved to header bar */}

          {viewMode === 'hitters' ? (
            <>
              {/* Outfield fence arc */}
              <svg style={{ position: 'absolute', left: '50%', top: '6%', transform: 'translateX(-50%)', width: 660, height: 260, pointerEvents: 'none' }} viewBox="0 0 660 260">
                <path d="M 40 255 Q 330 15 620 255" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={2.5} />
              </svg>

              {/* Diamond SVG */}
              <svg style={{ position: 'absolute', left: '50%', top: '38%', transform: 'translate(-50%, 0)', pointerEvents: 'none' }} width="300" height="340" viewBox="0 0 300 340">
                <polygon points="150,20 270,140 150,260 30,140" fill="rgba(27,77,27,0.4)" />
                <polygon points="150,10 280,140 150,270 20,140" fill="none" stroke="#eab308" strokeWidth="4" />
                <circle cx="150" cy="140" r="30" fill="rgba(120,113,108,0.35)" />
                <rect x="141" y="1" width="18" height="18" fill="#fff" transform="rotate(45,150,10)" />
                <rect x="271" y="131" width="18" height="18" fill="#fff" transform="rotate(45,280,140)" />
                <rect x="11" y="131" width="18" height="18" fill="#fff" transform="rotate(45,20,140)" />
                <circle cx="150" cy="270" r="40" fill="rgba(120,113,108,0.35)" />
                <polygon points="138,258 162,258 162,274 150,286 138,274" fill="#fff" />
              </svg>

              {/* Player tiles on diamond */}
              {diamondPlayers.map(player => {
                const slot = player._slot;
                const pos = TILE_POSITIONS[slot];
                if (!pos) return null;
                return (
                  <OptimizerPlayerTile
                    key={player.ids?.mlb || player.info?.full_name}
                    player={player}
                    posStyle={pos}
                    dotColor={getDotColor(player)}
                    isActive={flyoutPlayer?.ids?.mlb === player.ids?.mlb}
                    onClick={openFlyout}
                    categories={optimizerData?.meta?.categories}
                  />
                );
              })}

              {/* Bottom row */}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                display: 'flex', justifyContent: 'center',
                background: colors.neutral[800],
                borderTop: `1px solid ${colors.neutral[700]}`,
              }}>
                {bottomRowPlayers.map((player, i) => (
                  <div
                    key={player.ids?.mlb || i}
                    onClick={() => openFlyout(player)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      padding: '6px 20px',
                      borderRight: i < bottomRowPlayers.length - 1 ? `1px solid ${colors.neutral[700]}` : 'none',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = colors.neutral[700]; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors.primary, letterSpacing: 0.5 }}>
                      {getBasePosition(player.league_context?.roster?.position) || '—'}
                    </span>
                    <span style={{ fontSize: 12, color: colors.neutral[300], whiteSpace: 'nowrap' }}>
                      <PlayerLink mlbId={player.ids?.mlb} name={player.info?.full_name || '—'} style={{ color: colors.neutral[300], fontSize: 12 }} />
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <BullpenView pitchers={pitcherPlayers} />
          )}

          {/* Flyout panel */}
          {flyoutPlayer && (
            <OptimizerFlyout
              player={flyoutPlayer}
              alternatives={flyoutData}
              loading={flyoutLoading}
              optimizerData={optimizerData}
              onClose={closeFlyout}
              onViewTrade={(rec) => setTradeDetailRec(rec)}
              onPickup={(fa) => openPlayerModal && openPlayerModal(fa?.ids?.mlb, fa)}
              selectedCandidate={selectedCandidate}
              onSelectCandidate={setSelectedCandidate}
            />
          )}
        </div>
        ) : subView === 'gaps' ? (
          <OptimizerGapAnalysis optimizerData={optimizerData} league={league} userTeam={userTeam} teams={teams}
            leagueId={leagueId} selectedCandidate={selectedCandidate} subjectPlayer={flyoutPlayer}
            onCategorySelect={(cat) => { setSeedCategory(cat); setSubView('sequences'); }} />
        ) : subView === 'sequences' ? (
          <>
            {/* Targeted trade search */}
            <div ref={targetedSearchRef} style={{ position: 'relative', marginBottom: 12 }}>
              <div style={{
                display: 'flex', gap: 8, alignItems: 'center',
                padding: '8px 12px', background: colors.neutral[850], borderRadius: 6,
                border: `1px solid ${colors.neutral[700]}`,
              }}>
                <select
                  value={targetedDirection}
                  onChange={e => { setTargetedDirection(e.target.value); clearTargeted(); }}
                  style={{
                    background: colors.neutral[800], color: colors.neutral[300],
                    border: `1px solid ${colors.neutral[600]}`, borderRadius: 4,
                    padding: '4px 8px', fontSize: 12,
                  }}
                >
                  <option value="acquire">I want to acquire...</option>
                  <option value="send">I want to trade away...</option>
                </select>
                <input
                  type="text"
                  placeholder={targetedDirection === 'acquire' ? "Search player or select pick..." : "Search player name..."}
                  onFocus={() => { if (targetedDirection === 'acquire') setTargetedDropdownOpen(true); }}
                  value={targetedQuery}
                  onChange={handleTargetedQueryChange}
                  onKeyDown={handleTargetedKeyDown}
                  readOnly={!!targetedSelected}
                  style={{
                    flex: 1, background: colors.neutral[800], color: targetedSelected ? colors.primary : colors.neutral[200],
                    border: `1px solid ${colors.neutral[600]}`, borderRadius: 4,
                    padding: '4px 10px', fontSize: 12, outline: 'none',
                    fontWeight: targetedSelected ? 600 : 400,
                  }}
                />
                {targetedSelected && (
                  <button
                    onClick={clearTargeted}
                    style={{
                      background: 'transparent', color: colors.neutral[400],
                      border: 'none', padding: '2px 6px', fontSize: 14,
                      cursor: 'pointer', lineHeight: 1,
                    }}
                    title="Clear selection"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Autocomplete dropdown */}
              {targetedDropdownOpen && (targetedDropdown.length > 0 || (targetedDirection === 'acquire' && targetedQuery.length < 2)) && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  marginTop: 4, background: colors.neutral[800],
                  border: `1px solid ${colors.neutral[600]}`, borderRadius: 6,
                  maxHeight: 340, overflowY: 'auto', zIndex: 50,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                }}>
                  {targetedDropdown.map((player, i) => (
                    <div
                      key={player.ids?.mlb || i}
                      onClick={() => handleTargetedSelect(player)}
                      onMouseEnter={() => setTargetedHighlight(i)}
                      style={{
                        padding: '8px 12px', cursor: 'pointer',
                        background: i === targetedHighlight ? colors.neutral[700] : 'transparent',
                        borderBottom: i < targetedDropdown.length - 1 ? `1px solid ${colors.neutral[700]}` : 'none',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}
                    >
                      <div>
                        <span style={{ color: colors.neutral[200], fontSize: 13, fontWeight: 500 }}>
                          {player.info?.full_name}
                        </span>
                        <span style={{ color: colors.neutral[500], fontSize: 11, marginLeft: 8 }}>
                          {positionLabel(player)}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ color: colors.neutral[400], fontSize: 11 }}>
                          {player.league_context?.team?.team_name || 'FA'}
                        </span>
                        {player.league_context?.financial?.contract_salary != null && (
                          <span style={{ color: colors.success, fontSize: 11, marginLeft: 8 }}>
                            ${player.league_context.financial.contract_salary}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Draft pick options (acquire mode only) */}
                  {targetedDirection === 'acquire' && (() => {
                    const draftYear = league?.current_season || 2026;
                    const pickOptions = [];
                    for (let rd = 1; rd <= 8; rd++) {
                      pickOptions.push({ round: rd, year: draftYear, label: `Rd ${rd} pick (${draftYear})` });
                    }
                    return (
                      <div style={{ borderTop: `1px solid ${colors.neutral[700]}` }}>
                        <div style={{ fontSize: 10, color: colors.neutral[500], padding: '6px 12px 2px', fontWeight: 700, letterSpacing: 0.5 }}>
                          DRAFT PICKS
                        </div>
                        {pickOptions.map(pick => (
                          <div
                            key={`pick-${pick.round}-${pick.year}`}
                            onClick={() => handlePickSelect(pick)}
                            style={{
                              padding: '6px 12px', cursor: 'pointer',
                              color: colors.neutral[300], fontSize: 13,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = colors.neutral[700]}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            {pick.label}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Show targeted results, loading, or autonomous recs */}
            {targetedLoading ? (
              <OptimizerLoadingBar isLoading={true} />
            ) : targetedResults ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: colors.neutral[400] }}>
                    {targetedDirection === 'acquire' ? 'Trades to acquire' : 'Trades involving'}{' '}
                    <strong style={{ color: colors.primary }}>{targetedSelected?.info?.full_name}</strong>
                  </span>
                  <button
                    onClick={clearTargeted}
                    style={{
                      background: 'transparent', color: colors.neutral[400],
                      border: `1px solid ${colors.neutral[600]}`, borderRadius: 4,
                      padding: '2px 8px', fontSize: 11, cursor: 'pointer',
                    }}
                  >
                    Clear
                  </button>
                </div>
                <OptimizerTradeRecs
                  optimizerData={{ trade_engine: targetedResults.trade_engine }}
                  league={league} userTeam={userTeam} teams={teams}
                />
              </div>
            ) : (
              <OptimizerTradeRecs optimizerData={optimizerData} league={league} userTeam={userTeam} teams={teams}
                initialCatFilter={seedCategory} />
            )}
          </>
        ) : null}
      </div>

      {/* RIGHT: Sidebar */}
      <div style={{
        width: 260, background: colors.neutral[900],
        borderLeft: `1px solid ${colors.neutral[700]}`,
        padding: '16px 14px',
        overflowY: 'auto',
      }}>
        {/* Category Gaps — DD-RO-GAPS-CLUSTER-0728: labeled with the live toggle basis (Root A: every surface
            names its basis, no silent contradiction with the projected Action Priority list). */}
        <div style={{ fontSize: 12, fontWeight: 700, color: colors.primary, letterSpacing: 1.2, marginBottom: 8 }}
             data-testid="sidebar-gaps-header">
          CATEGORY GAPS <span style={{ color: colors.neutral[500], fontWeight: 600, letterSpacing: 0.4 }}>({_basisLabel})</span>
        </div>

        {/* DD-RO-UI-0727: the 3-way basis toggle, brought to the sidebar. Current (authoritative standings) is
            the default; Projected = stand-pat finish; With selection = finish with the highlighted FA. */}
        <div style={{ display: 'flex', gap: 3, marginBottom: 8 }}>
          {[{ key: 'current', label: 'Current' }, { key: 'projected', label: 'Projected' },
            { key: 'projected_selection', label: 'With selection' }].map(b => {
            const disabled = b.key === 'projected_selection' && (selectedCandidate?.ids?.mlb ?? null) == null;
            const active = gapBasis === b.key && !disabled;
            return (
              <button key={b.key} type="button" disabled={disabled}
                onClick={() => setGapBasis(b.key)}
                title={disabled ? 'Select an FA candidate to preview its projected standings' : ''}
                style={{ flex: 1, fontSize: 9, fontWeight: 700, padding: '3px 2px', borderRadius: 3,
                  cursor: disabled ? 'default' : 'pointer', border: `1px solid ${active ? colors.primary : colors.neutral[700]}`,
                  background: active ? colors.primary : 'transparent',
                  color: active ? colors.neutral[900] : disabled ? colors.neutral[600] : colors.neutral[400] }}>
                {b.label}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 9, color: colors.neutral[500], marginBottom: 8 }}>
          {gapBasis === 'current' ? 'authoritative standings' : 'accrued + rest-of-season projection'}
        </div>

        {/* DD-RO-BUGS2-0724 Bug 3: render BOTH sides, worst rank first (authoritative rank), so the most
            actionable gap surfaces regardless of the Field-View toggle. Rank/gap are basis-driven (GapRow). */}
        {[...hittingGaps, ...pitchingGaps]
          .map(gap => ({ gap, r: gapStandings?.categories?.[gap.category]?.rank ?? gap.current_rank ?? 0 }))
          .sort((a, b) => (b.r || 0) - (a.r || 0))
          .map(({ gap }) => (
            <GapRow key={gap.category} gap={gap} />
          ))}

        {/* Legend */}
        <div style={{ display: 'flex', gap: 10, marginTop: 8, fontSize: 11, color: colors.neutral[500] }}>
          {[
            { color: colors.success, label: 'Top 3' },
            { color: colors.warning, label: '4th-7th' },
            { color: colors.error, label: '8th+' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: item.color }} />
              {item.label}
            </div>
          ))}
        </div>

        {/* Standings divider */}
        <div style={{ height: 1, background: colors.neutral[700], margin: '16px 0 12px' }} />

        {/* Mini Standings — DD-RO-GAPS-CLUSTER-0728 4.1: follows the basis toggle, labeled with the live basis. */}
        <div style={{ fontSize: 12, fontWeight: 700, color: colors.primary, letterSpacing: 1.2, marginBottom: 8 }}
             data-testid="sidebar-standings-header">
          STANDINGS <span style={{ color: colors.neutral[500], fontWeight: 600, letterSpacing: 0.4 }}>({_basisLabel})</span>
        </div>

        {basisStandings.length === 0 ? (
          <div style={{ fontSize: 11, color: colors.error, padding: '4px 0' }} data-testid="sidebar-standings-error">
            Standings for the {_basisLabel.toLowerCase()} basis are unavailable.
          </div>
        ) : basisStandings.map((team, i) => {
          const isUser = team.team_id === userTeam?.team_id;
          return (
            <div key={team.team_id} style={{ display: 'flex', alignItems: 'center', padding: '3px 0', fontSize: 12 }}>
              <span style={{ color: colors.neutral[500], width: 18 }}>{team.rank ?? i + 1}</span>
              <span style={{
                flex: 1,
                color: isUser ? colors.primary : colors.neutral[400],
                fontWeight: isUser ? 700 : 400,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {team.team_name}
              </span>
              <span style={{
                fontWeight: isUser ? 700 : 600,
                color: isUser ? colors.primary : colors.neutral[300],
                textAlign: 'right',
              }}>
                {team.total_points ?? 0}
              </span>
            </div>
          );
        })}
      </div>

      {/* Trade detail modal — DD-RO-BUGS3-0724 Item 4: OptimizerTradeDetail reads `sequence`
          (and `teams`), not `recommendation`; the old prop name left sequence undefined so the
          modal bailed (`if (!sequence) return null`) and clicking a flyout trade opened nothing.
          The flyout now hands over a real trade_engine sequence, so this renders it directly. */}
      {tradeDetailRec && (
        <OptimizerTradeDetail
          sequence={tradeDetailRec}
          teams={teams}
          onClose={() => setTradeDetailRec(null)}
        />
      )}
    </div>
  );
};

export default OptimizerDashboard;
